import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { toast } from 'sonner';
import { Radio, Pause, Play, FastForward, SkipForward, Zap, MicOff, Mic, Square, Flag, Clock, ShieldAlert, ChevronDown, ChevronUp, UserCheck, Bot, BookOpen, PhoneCall, MessageSquare, Gauge, Eye, Timer, PauseCircle, PlayCircle, Lock } from 'lucide-react';
import { assignDeviceViaN8n } from '../../services/n8n';
import { useRoomDeviceBinding } from '../../hooks/useRoomDeviceBinding';
import RoomBindingCard from '../../components/tabletop/RoomBindingCard';
import SeatDeviceSelect from '../../components/tabletop/SeatDeviceSelect';
import {
  tabletopRunAPI,
  tabletopActsAPI,
  tabletopInjectsAPI,
  tabletopPersonasAPI,
  TabletopExercise,
  TabletopRunMeta,
  TabletopSeatState,
  TabletopRunEvent,
  TabletopAct,
  TabletopInject,
  TabletopFlag,
  TabletopPersona,
} from '../../services/tabletopService';

const CONTROL_ICON: Record<string, React.ElementType> = {
  human: UserCheck,
  ai_driven: Bot,
  referenced_only: BookOpen,
};

interface Props {
  projectId: string;
  exercise: TabletopExercise;
  onExerciseChange: (exercise: TabletopExercise) => void;
}

const POLL_MS = 5000;

// Most event kinds carry a human-readable payload worth showing directly
// instead of raw JSON — everything else (organiser_action, clock_transition
// etc.) is infrequent/technical enough that the JSON fallback is fine.
function formatEventDetail(event: TabletopRunEvent): string {
  const detail = event.detail as Record<string, unknown>;
  if (event.kind === 'utterance' && typeof detail.text === 'string') {
    return detail.speaker === 'human' ? `"${detail.text}"` : detail.text;
  }
  if (event.kind === 'inject_delivered' && typeof detail.content === 'string') {
    return detail.content;
  }
  return JSON.stringify(detail).slice(0, 220);
}

function formatSimTime(ms: number): string {
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `T+${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

// Advisory only — mirrors the wall-clock windows and close-early signal
// tabletop-clock.service.ts's speakAs actually enforces server-side (a stale
// indicator here just means a facilitator's Speak/Summon click gets a 409 a
// beat later, not a real inconsistency).
const DEVICE_VOICE_SPEAKING_WINDOW_MS = 5 * 60_000;
const BROWSER_TTS_SPEAKING_WINDOW_MS = 45_000;

function isSeatSpeaking(seat: TabletopSeatState, events: TabletopRunEvent[]): boolean {
  if (seat.deliveryChannel === 'text') return false;
  const windowMs = seat.deliveryChannel === 'device_voice' ? DEVICE_VOICE_SPEAKING_WINDOW_MS : BROWSER_TTS_SPEAKING_WINDOW_MS;
  const speakingEvents = events.filter(e => e.kind === 'persona_speaking' && e.personaId === seat.personaId);
  if (speakingEvents.length === 0) return false;
  const latestAt = Math.max(...speakingEvents.map(e => new Date(e.wallClockAt).getTime()));
  if (Date.now() - latestAt >= windowMs) return false;
  const closedByTranscript = events.some(
    e => e.kind === 'voice_conversation_ended' && e.personaId === seat.personaId && new Date(e.wallClockAt).getTime() >= latestAt
  );
  return !closedByTranscript;
}

function actionErrorMessage(err: any): string {
  return err?.response?.data?.error || 'Action failed';
}

// Countdown display for an adjudicator deadline — deliberately distinct from
// formatSimTime's "T+HH:MM" clock-face style, since this is a duration until
// something happens, not a point in sim time.
function formatCountdown(remainingMs: number): string {
  if (remainingMs <= 0) return 'due now';
  const totalMinutes = Math.round(remainingMs / 60000);
  if (totalMinutes < 1) return '<1m';
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

const PRESSURE_BAR_COLOR = ['bg-slate-300', 'bg-yellow-300', 'bg-amber-400', 'bg-orange-500', 'bg-red-600'];
const DEFAULT_ADJUDICATION_BUDGET = 120;

// RunConsolePage is the organiser's live controls for a STARTEXed run (plan
// section 8 / 4.3). It polls run state and the event log every 5s — the same
// interval as the backend clock's own tick, so the console never looks
// staler than the clock itself actually is.
const RunConsolePage: React.FC<Props> = ({ projectId, exercise, onExerciseChange }) => {
  const runId = exercise.currentRunId;
  const [run, setRun] = useState<TabletopRunMeta | null>(null);
  const [simTimeMs, setSimTimeMs] = useState(0);
  const [seats, setSeats] = useState<TabletopSeatState[]>([]);
  const [flags, setFlags] = useState<TabletopFlag[]>([]);
  const [events, setEvents] = useState<TabletopRunEvent[]>([]);
  const [acts, setActs] = useState<TabletopAct[]>([]);
  const [injects, setInjects] = useState<TabletopInject[]>([]);
  const [personas, setPersonas] = useState<TabletopPersona[]>([]);
  const [curveballText, setCurveballText] = useState('');
  const [curveballPersonaId, setCurveballPersonaId] = useState('');
  const [speakOpenSeat, setSpeakOpenSeat] = useState<string | null>(null);
  const [speakDraft, setSpeakDraft] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [expandedInject, setExpandedInject] = useState<number | null>(null);
  const [showRoomCard, setShowRoomCard] = useState(false);
  const [pairingPersonaId, setPairingPersonaId] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Lets Elizabeth (or whoever's running the console) reassign a seat's
  // device/avatar mid-run — Cast Setup already covers before STARTEX, this
  // covers during. Same room-then-device allocation, not a separate scheme.
  const binding = useRoomDeviceBinding(exercise.sessionId);

  const poll = useCallback(async () => {
    if (!runId) return;
    const [viewRes, eventsRes] = await Promise.all([
      tabletopRunAPI.get(projectId, runId),
      tabletopRunAPI.events(projectId, runId),
    ]);
    setRun(viewRes.data.run);
    setSimTimeMs(viewRes.data.simTimeMs);
    setSeats(viewRes.data.seats);
    setFlags(viewRes.data.flags);
    setEvents(eventsRes.data.events.slice().sort((a, b) => b.simTimeMs - a.simTimeMs));
  }, [projectId, runId]);

  useEffect(() => {
    if (!runId) return;
    poll();
    Promise.all([
      tabletopActsAPI.list(projectId, exercise.exerciseId),
      tabletopInjectsAPI.list(projectId, exercise.exerciseId),
      tabletopPersonasAPI.list(projectId, exercise.exerciseId),
    ]).then(([actsRes, injectsRes, personasRes]) => {
      setActs(actsRes.data.acts.sort((a, b) => a.simTimeStartMs - b.simTimeStartMs));
      setInjects(injectsRes.data.injects.sort((a, b) => a.seq - b.seq));
      setPersonas(personasRes.data.personas);
    });
    pollRef.current = setInterval(poll, POLL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [runId, poll, projectId, exercise.exerciseId]);

  const withBusy = async (key: string, action: () => Promise<void>) => {
    setBusy(key);
    try {
      await action();
      await poll();
    } finally {
      setBusy(null);
    }
  };

  // Speak/Summon/curveball-with-persona can all hit speakAs's conversation-
  // collision 409 (plan 02 design note) — this project's global axios
  // interceptor only toasts 5xx/network errors, so a 4xx like that one needs
  // its own surfacing here rather than failing silently.
  const withBusyToasted = async (key: string, action: () => Promise<void>) => {
    setBusy(key);
    try {
      await action();
      await poll();
    } catch (err: any) {
      toast.error(actionErrorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const reassignDevice = async (seat: TabletopSeatState, deviceId: string) => {
    if (!exercise.sessionId || !runId) return;
    setPairingPersonaId(seat.personaId);
    try {
      if (deviceId) await assignDeviceViaN8n(deviceId, seat.personaId, exercise.sessionId);
      await tabletopRunAPI.setSeatDevice(projectId, runId, seat.personaId, deviceId || undefined);
      await Promise.all([binding.reload(), poll()]);
    } finally {
      setPairingPersonaId(null);
    }
  };

  // Comms view (plan 05) — private portal-chat threads, derived client-side
  // from the same unfiltered event log the console already polls (the
  // second screen and participant views are the ones that filter `channel:
  // 'private'` out; the organiser is allowed to see that traffic exists,
  // just never on the wall — see tabletop-05's "Do not put private text on
  // the second screen"). Ranks "latest" by wallClockAt, not by position in
  // the (already simTimeMs-sorted) `events` array or by simTimeMs itself —
  // a send/reply pair shares the same simTimeMs (both events are written
  // during the same synchronous request), and a stable sort on a tied key
  // preserves write order, which would otherwise surface the human's own
  // message as "latest" instead of the persona's reply that came after it.
  const commsThreads = useMemo(() => {
    const byThread = new Map<string, { threadKey: string; personaAId: string; personaBId: string; count: number; latestText: string; latestAtMs: number }>();
    for (const event of events) {
      if (event.kind !== 'utterance') continue;
      const detail = event.detail as Record<string, unknown>;
      if (detail?.channel !== 'private') continue;
      const threadKey = detail.threadKey as string | undefined;
      if (!threadKey) continue;
      const wallClockAtMs = new Date(event.wallClockAt).getTime();
      const existing = byThread.get(threadKey);
      if (existing) {
        existing.count += 1;
        if (wallClockAtMs > existing.latestAtMs) {
          existing.latestText = (detail.text as string) || '';
          existing.latestAtMs = wallClockAtMs;
        }
      } else {
        const [personaAId, personaBId] = threadKey.split('::');
        byThread.set(threadKey, {
          threadKey, personaAId, personaBId, count: 1,
          latestText: (detail.text as string) || '',
          latestAtMs: wallClockAtMs,
        });
      }
    }
    return Array.from(byThread.values()).sort((a, b) => b.latestAtMs - a.latestAtMs);
  }, [events]);

  if (!runId) {
    return <div className="flex-1 flex items-center justify-center text-slate-400 text-sm p-8">This exercise has no active run.</div>;
  }
  if (!run) {
    return <div className="flex-1 flex items-center justify-center text-slate-400 text-sm p-8">Loading run…</div>;
  }

  const currentAct = acts.find(a => a.actId === run.currentActId);
  const personaById = new Map(personas.map(p => [p.personaId, p]));

  // Adjudicator panel derivations (plan 04). `events` is already sorted
  // newest-first by the poll() above, so the first matching entry is the
  // latest adjudication — no re-sort needed.
  const latestAdjudicationEvent = events.find(e => e.kind === 'adjudication');
  const latestObservation = (latestAdjudicationEvent?.detail as { observation?: string } | undefined)?.observation || '';
  const pressureLevel = run.pressureLevel ?? 0;
  const activeDeadlines = (run.adjudicationDeadlines || []).filter(d => d.atSimMs > simTimeMs);
  const adjudicationBudget = run.adjudicationBudget ?? DEFAULT_ADJUDICATION_BUDGET;
  const adjudicationBudgetRemaining = Math.max(0, adjudicationBudget - (run.adjudicationCallsUsed ?? 0));

  return (
    <div className="flex-1 overflow-y-auto p-6 font-display bg-slate-50">
      <div className="w-full max-w-[2000px] mx-auto flex flex-col gap-4">

        {/* ── Top bar: identity + clock ─────────────────────────────────── */}
        <header className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary flex-shrink-0">
              <Radio size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Run Console</h1>
              <p className="text-slate-500 text-sm font-medium">
                {run.status === 'held' ? 'HELD' : run.status === 'complete' ? 'ENDEX' : 'RUNNING'} · {currentAct?.label || run.currentActId}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-navy text-white rounded-xl px-5 py-2.5">
            <Clock size={16} />
            <span className="text-lg font-mono font-bold">{formatSimTime(simTimeMs)}</span>
            <span className="text-xs text-slate-300">×{run.compressionRatio}</span>
          </div>
        </header>

        {run.pendingInjectsOnResume !== undefined && run.status === 'held' && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 px-5 py-4 text-sm text-amber-800">
            Run resumed after a backend restart — {run.pendingInjectsOnResume} inject(s) are pending. Review before resuming.
          </div>
        )}

        {/* ── Control bar ────────────────────────────────────────────────── */}
        <div className="bg-white border border-[#e2e8f0] rounded-2xl p-4 flex flex-wrap items-center gap-3">
          {run.status === 'running' ? (
            <button onClick={() => withBusy('hold', async () => { await tabletopRunAPI.hold(projectId, runId); })} disabled={!!busy}
              className="flex items-center gap-2 rounded-xl h-10 px-4 bg-white border border-slate-200 text-slate-600 text-sm font-bold hover:bg-slate-50 disabled:opacity-40">
              <Pause size={16} /> Hold
            </button>
          ) : run.status === 'held' ? (
            <button onClick={() => withBusy('resume', async () => { await tabletopRunAPI.resume(projectId, runId); })} disabled={!!busy}
              className="flex items-center gap-2 rounded-xl h-10 px-4 bg-primary text-white text-sm font-bold hover:opacity-90 disabled:opacity-40">
              <Play size={16} /> Resume
            </button>
          ) : null}

          <div className="flex items-center gap-2">
            <FastForward size={14} className="text-slate-400" />
            <select
              value={run.compressionRatio}
              onChange={e => withBusy('ratio', async () => { await tabletopRunAPI.setRatio(projectId, runId, Number(e.target.value)); })}
              disabled={!!busy}
              className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs text-navy"
            >
              {[1, 5, 15, 30, 60, 120].map(r => <option key={r} value={r}>×{r}</option>)}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <SkipForward size={14} className="text-slate-400" />
            <select
              value={run.currentActId}
              onChange={e => withBusy('skip', async () => { await tabletopRunAPI.skipToAct(projectId, runId, e.target.value); })}
              disabled={!!busy}
              className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs text-navy"
            >
              {acts.map(a => <option key={a.actId} value={a.actId}>{a.label}</option>)}
            </select>
          </div>

          <button
            onClick={() => setShowRoomCard(v => !v)}
            className={`flex items-center gap-2 rounded-xl h-10 px-4 border text-sm font-bold transition-colors ${showRoomCard ? 'bg-primary/10 border-primary/30 text-primary' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
          >
            <Radio size={16} /> Devices
          </button>

          <button
            onClick={() => window.confirm('End this exercise? This cannot be undone.') && withBusy('endex', async () => {
              const r = await tabletopRunAPI.endex(projectId, runId);
              onExerciseChange({ ...exercise, status: 'complete' });
              setRun(r.data.run);
            })}
            disabled={!!busy || run.status === 'complete'}
            className="flex items-center gap-2 rounded-xl h-10 px-4 border border-slate-200 text-slate-600 text-sm font-bold hover:bg-slate-50 disabled:opacity-40 ml-auto"
          >
            <Flag size={16} /> ENDEX
          </button>
          <button
            onClick={() => window.confirm('Emergency stop — end the run immediately?') && withBusy('emergency', async () => {
              const r = await tabletopRunAPI.emergencyStop(projectId, runId);
              onExerciseChange({ ...exercise, status: 'complete' });
              setRun(r.data.run);
            })}
            disabled={!!busy || run.status === 'complete'}
            className="flex items-center gap-2 rounded-xl h-10 px-4 bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-40"
          >
            <Square size={16} /> Emergency Stop
          </button>
        </div>

        {showRoomCard && <RoomBindingCard binding={binding} />}

        {/* ── Main dashboard: seats | event log | flags/injects/curveball ── */}
        <div className="grid grid-cols-1 xl:grid-cols-[300px_minmax(0,1fr)_380px] gap-4 items-start">

          {/* Seats */}
          <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden xl:sticky xl:top-6">
            <div className="px-4 py-3 border-b border-[#e2e8f0] flex items-center justify-between">
              <h2 className="text-sm font-bold text-navy">Seats ({seats.length})</h2>
            </div>
            <div className="divide-y divide-slate-100 max-h-[calc(100vh-320px)] overflow-y-auto">
              {seats.map(seat => {
                const persona = personaById.get(seat.personaId);
                const ControlIcon = CONTROL_ICON[seat.control] || Bot;
                return (
                  <div key={seat.personaId} className="px-4 py-2.5 flex flex-col gap-2">
                    <div className="min-w-0 flex items-center justify-between gap-2">
                      <div className="min-w-0 flex items-center gap-2">
                        <ControlIcon size={13} className="text-slate-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm text-navy font-medium truncate">{persona?.displayName || seat.personaId}</p>
                          {persona?.category && <p className="text-[10px] text-slate-400 uppercase tracking-wide truncate">{persona.category.replace(/_/g, ' ')}</p>}
                        </div>
                      </div>
                      <button
                        onClick={() => withBusy(`mute-${seat.personaId}`, async () => { await tabletopRunAPI.muteSeat(projectId, runId, seat.personaId, !seat.muted); })}
                        className={`flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full flex-shrink-0 ${seat.muted ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-500'}`}
                      >
                        {seat.muted ? <MicOff size={11} /> : <Mic size={11} />}
                        {seat.muted ? 'Muted' : 'Live'}
                      </button>
                    </div>
                    {showRoomCard && seat.deliveryChannel === 'device_voice' && (
                      <SeatDeviceSelect
                        personaId={seat.personaId}
                        value={seat.deviceId}
                        roomId={binding.roomId}
                        roomDevices={binding.roomDevices}
                        allDevices={binding.allDevices}
                        disabled={pairingPersonaId === seat.personaId}
                        onChange={deviceId => reassignDevice(seat, deviceId)}
                      />
                    )}
                    {seat.control === 'ai_driven' && (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <button
                          onClick={() => { setSpeakOpenSeat(speakOpenSeat === seat.personaId ? null : seat.personaId); setSpeakDraft(''); }}
                          className="flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200"
                        >
                          <MessageSquare size={10} /> Speak
                        </button>
                        <button
                          onClick={() => withBusyToasted(`summon-${seat.personaId}`, async () => { await tabletopRunAPI.summon(projectId, runId, seat.personaId); })}
                          disabled={!!busy}
                          className="flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-40"
                        >
                          <PhoneCall size={10} /> Summon
                        </button>
                        {isSeatSpeaking(seat, events) && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary">Speaking…</span>
                        )}
                      </div>
                    )}
                    {speakOpenSeat === seat.personaId && (
                      <div className="flex items-center gap-1.5">
                        <input
                          autoFocus
                          value={speakDraft}
                          onChange={e => setSpeakDraft(e.target.value)}
                          placeholder="Say as this persona…"
                          className="flex-1 min-w-0 rounded-lg border border-slate-200 px-2 py-1 text-xs text-navy"
                        />
                        <button
                          onClick={() => speakDraft.trim() && withBusyToasted(`speak-${seat.personaId}`, async () => {
                            await tabletopRunAPI.speak(projectId, runId, seat.personaId, speakDraft.trim());
                            setSpeakDraft('');
                            setSpeakOpenSeat(null);
                          })}
                          disabled={!speakDraft.trim() || !!busy}
                          className="text-[10px] font-bold text-white bg-primary rounded-lg px-2 py-1.5 disabled:opacity-40 flex-shrink-0"
                        >
                          Send
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              {seats.length === 0 && <p className="px-4 py-8 text-sm text-slate-400 text-center">No seats.</p>}
            </div>
          </div>

          {/* Event log — the main focus */}
          <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
            <div className="px-5 py-3 border-b border-[#e2e8f0]">
              <h2 className="text-sm font-bold text-navy">Event log ({events.length})</h2>
            </div>
            <div className="divide-y divide-slate-100 max-h-[calc(100vh-320px)] overflow-y-auto">
              {events.map(event => (
                <div key={event.eventId} className="px-5 py-3 flex items-start gap-3 text-xs">
                  <span className="font-mono text-slate-400 w-14 flex-shrink-0 pt-0.5">{formatSimTime(event.simTimeMs)}</span>
                  <span className="font-semibold text-navy w-28 flex-shrink-0 uppercase tracking-wide pt-0.5">{event.kind.replace(/_/g, ' ')}</span>
                  <span className="text-slate-600 leading-relaxed">
                    {event.personaId && <span className="font-medium text-slate-800">{personaById.get(event.personaId)?.displayName || event.personaId} — </span>}
                    {formatEventDetail(event)}
                  </span>
                </div>
              ))}
              {events.length === 0 && <p className="px-5 py-8 text-sm text-slate-400 text-center">No events yet.</p>}
            </div>
          </div>

          {/* Flags, fire-inject-now, curveball */}
          <div className="flex flex-col gap-4">

            {/* Adjudicator panel (plan 04) — pressure, latest reasoning,
                deadlines and the suspend toggle, so a facilitator can see why
                the scenario just moved rather than trusting it blind. */}
            <div className="bg-white border border-[#e2e8f0] rounded-2xl p-4 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wide text-navy flex items-center gap-1.5">
                  <Gauge size={13} /> Adjudicator
                </p>
                <button
                  onClick={() => withBusyToasted('adjudication-toggle', async () => {
                    if (run.adjudicationSuspended) await tabletopRunAPI.resumeAdjudication(projectId, runId);
                    else await tabletopRunAPI.suspendAdjudication(projectId, runId);
                  })}
                  disabled={!!busy}
                  className={`flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full disabled:opacity-40 ${run.adjudicationSuspended ? 'bg-slate-100 text-slate-500' : 'bg-primary/10 text-primary'}`}
                >
                  {run.adjudicationSuspended ? <PlayCircle size={11} /> : <PauseCircle size={11} />}
                  {run.adjudicationSuspended ? 'Suspended' : 'Active'}
                </button>
              </div>

              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className={`h-full ${PRESSURE_BAR_COLOR[pressureLevel]}`} style={{ width: `${(pressureLevel / 4) * 100}%` }} />
                </div>
                <span className="text-[11px] font-mono text-slate-500 flex-shrink-0">P{pressureLevel}</span>
              </div>
              {run.pressureReason && <p className="text-xs text-slate-500">{run.pressureReason}</p>}

              {latestObservation && (
                <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 flex items-center gap-1"><Eye size={10} /> Latest reasoning</p>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">{latestObservation}</p>
                </div>
              )}

              {activeDeadlines.length > 0 && (
                <div className="flex flex-col gap-1.5">
                  {activeDeadlines.map((d, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 text-xs">
                      <span className="text-slate-600 flex items-center gap-1 min-w-0">
                        <Timer size={11} className="text-slate-400 flex-shrink-0" />
                        <span className="truncate">{d.label}</span>
                      </span>
                      <span className="font-mono text-slate-500 flex-shrink-0">{formatCountdown(d.atSimMs - simTimeMs)}</span>
                    </div>
                  ))}
                </div>
              )}

              <p className="text-[10px] text-slate-400">Budget: {adjudicationBudgetRemaining} of {adjudicationBudget} adjudications remaining</p>
            </div>

            {/* Comms — private portal-chat threads (plan 05). Turn counts and
                latest message only, so a facilitator can see who's quietly
                messaging whom without the wall showing it. */}
            <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
              <div className="px-4 py-3 border-b border-[#e2e8f0] flex items-center justify-between">
                <h2 className="text-sm font-bold text-navy flex items-center gap-1.5"><MessageSquare size={13} /> Comms ({commsThreads.length})</h2>
                <span className="flex items-center gap-1 text-[10px] font-semibold text-slate-400"><Lock size={10} /> Private</span>
              </div>
              <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
                {commsThreads.map(t => (
                  <div key={t.threadKey} className="px-4 py-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-navy truncate">
                        {personaById.get(t.personaAId)?.displayName || t.personaAId} ↔ {personaById.get(t.personaBId)?.displayName || t.personaBId}
                      </p>
                      <span className="text-[10px] text-slate-400 flex-shrink-0">{t.count} msg{t.count === 1 ? '' : 's'}</span>
                    </div>
                    <p className="text-xs text-slate-500 truncate mt-0.5">{t.latestText}</p>
                  </div>
                ))}
                {commsThreads.length === 0 && <p className="px-4 py-6 text-xs text-slate-400 text-center">No private messages yet.</p>}
              </div>
            </div>

            {flags.length > 0 && (
              <div className="rounded-2xl bg-red-50 border border-red-200 p-4 flex flex-col gap-2">
                <p className="text-xs font-bold uppercase tracking-wide text-red-600 flex items-center gap-1.5"><ShieldAlert size={13} /> Flags ({flags.length})</p>
                {flags.map(flag => (
                  <p key={flag.flagId} className="text-sm text-red-800"><span className="font-semibold">{flag.type.replace(/_/g, ' ')}:</span> {flag.detail}</p>
                ))}
              </div>
            )}

            <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
              <div className="px-4 py-3 border-b border-[#e2e8f0]">
                <h2 className="text-sm font-bold text-navy">Fire an inject now</h2>
              </div>
              <div className="p-3 flex flex-col gap-2 max-h-96 overflow-y-auto">
                {injects.map(inject => {
                  const expanded = expandedInject === inject.seq;
                  return (
                    <div key={inject.seq} className="rounded-lg border border-slate-200 overflow-hidden flex-shrink-0">
                      <button
                        onClick={() => setExpandedInject(expanded ? null : inject.seq)}
                        className="w-full text-left px-3 py-2 flex items-start gap-2 hover:bg-slate-50"
                      >
                        <span className="font-mono text-slate-400 text-xs mt-0.5 flex-shrink-0">#{inject.seq}</span>
                        <span className="flex-1 min-w-0">
                          <span className={`block text-xs text-navy ${expanded ? '' : 'line-clamp-2'}`}>{inject.content}</span>
                        </span>
                        {expanded ? <ChevronUp size={13} className="text-slate-400 flex-shrink-0 mt-0.5" /> : <ChevronDown size={13} className="text-slate-400 flex-shrink-0 mt-0.5" />}
                      </button>
                      {expanded && (
                        <div className="px-3 pb-2.5 flex justify-end">
                          <button
                            onClick={() => withBusy(`inject-${inject.seq}`, async () => { await tabletopRunAPI.injectNow(projectId, runId, inject.seq); })}
                            disabled={!!busy}
                            className="flex items-center gap-1.5 text-xs font-bold text-white bg-primary rounded-lg px-3 py-1.5 hover:opacity-90 disabled:opacity-40"
                          >
                            Fire now
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
                {injects.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No injects.</p>}
              </div>
            </div>

            <div className="bg-white border border-[#e2e8f0] rounded-2xl p-4 flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Zap size={16} className="text-primary flex-shrink-0" />
                <input
                  value={curveballText}
                  onChange={e => setCurveballText(e.target.value)}
                  placeholder="Curveball not on the script…"
                  className="flex-1 min-w-0 rounded-xl border border-slate-200 px-3 py-2 text-sm text-navy"
                />
                <button
                  onClick={() => curveballText.trim() && withBusyToasted('curveball', async () => {
                    await tabletopRunAPI.curveball(projectId, runId, curveballText.trim(), curveballPersonaId || undefined);
                    setCurveballText('');
                  })}
                  disabled={!curveballText.trim() || !!busy}
                  className="flex items-center gap-2 rounded-xl h-9 px-4 bg-primary text-white text-xs font-bold hover:opacity-90 disabled:opacity-40 flex-shrink-0"
                >
                  Send
                </button>
              </div>
              <select
                value={curveballPersonaId}
                onChange={e => setCurveballPersonaId(e.target.value)}
                className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs text-navy"
              >
                <option value="">No one — log only</option>
                {seats.filter(s => s.control === 'ai_driven').map(s => (
                  <option key={s.personaId} value={s.personaId}>{personaById.get(s.personaId)?.displayName || s.personaId}</option>
                ))}
              </select>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};

export default RunConsolePage;
